import React, { useEffect, useState } from "react";
import { Button, Card, CardBody, Col, Row, Form } from "react-bootstrap";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { LocationService } from "./service";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import { useTranslation } from "react-i18next";
import FormErrorSummary from "../../shared/components/FormErrorSummary";

const schema = yup.object().shape({
  name: yup.string().required('formErrors.nameRequired'),
  parent: yup.number().nullable(), // Include parent in the schema as nullable
});

interface LocationState {
  name: string;
  parent?: number | null;
}

const LocationForm: React.FC = () => {
  const { t } = useTranslation(); 
  const { id } = useParams<{ id: string }>(); 
  const [searchParams] = useSearchParams(); 
  const parentId = searchParams.get("parent"); 
  const isEdit = Boolean(id);
  const [locationData, setLocationData] = useState<LocationState | null>(null);
  const navigate = useNavigate();
  const { control, handleSubmit, reset, formState: { errors } } = useForm<LocationState>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: "",
      parent: parentId ? parseInt(parentId, 10) : undefined, 
    },
  });

  useEffect(() => {
    if (isEdit) {
      const fetchLocation = async () => {
        try {
          const response = await LocationService.getSingle(+id!);
          const location = response;
          setLocationData({
            name: location.name,
            parent: location.parent ? location.parent.id : undefined,
          });
        } catch (error) {
          ErrorToast(error);
        }
      };

      fetchLocation();
    }
  }, [isEdit, id]);

  useEffect(() => {
    if (locationData) {
      reset(locationData);
    }
  }, [locationData, reset]);

  const onSubmit = async (data: LocationState) => {
    try {
      const payload = {
        ...data,
        parent: parentId ? parseInt(parentId, 10) : null, // Include parent ID dynamically
      };

      if (isEdit) {
        await LocationService.update(payload, +id!);
      } else {
        await LocationService.create(payload);
      }
      
      SuccessToast(t(`location.${isEdit ? "successUpdate" : "successCreate"}`));
      navigate(`/locations${parentId ? `/${parentId}` : ""}`); // Redirect to the list page with parent ID if available
      reset();
    } catch (error) {
      ErrorToast(error);
    }
  };

  return (
    <React.Fragment>
      <Row>
        <Col sm={12}>
          <Card>
            <CardBody>
              <Form onSubmit={handleSubmit(onSubmit)}>
                <FormErrorSummary errors={errors} />
                <Row className="g-3">
                  <Col sm={12} xl={6}>
                    <div className="mb-0">
                      <label className="form-label">{t("location.name")}</label>
                      <Controller
                        name="name"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="text"
                            className={`form-control ${errors.name ? "is-invalid" : ""}`}
                            placeholder={t("location.enterName")}
                            {...field}
                          />
                        )}
                      />
                      {errors.name && <div className="invalid-feedback">
                        {t(errors.name.message as string)}
                      </div>}
                    </div>
                  </Col>
                  <Col sm={12}>
                    <Button type="submit" variant="primary">
                      {t(`location.${isEdit ? "update" : "create"}`)}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default LocationForm;